/**
 * "Start from a design" — turning a recipe into a ring SET the host restores
 * (band designs W4). Pure, so `scripts/check-ring-body.ts` pins it.
 *
 * A recipe goes through the HOST, never straight at the viewer: the host owns
 * the ring list, the arrangement and the undo history, and a ring added behind
 * its back is invisible to its ring switcher (W1 live finding 4). So a recipe
 * becomes a `RingSetDoc` and `RingBuilder` restores it with `replaceRingSet`
 * — the same path undo uses — and records the whole change as ONE step.
 */
import type { RingDesignPatch, RingState } from "../../../shared/api-contract";
import type { RingSetDoc } from "../ringState";
import type { RingRow } from "../RingManagerModal";
import { RECIPES, type RingRecipe } from "./recipeData";

export { RECIPES };
export type { RingRecipe };

/** The partner's default finger when the set has no second ring yet: a size and a half up. */
export const PARTNER_SIZE_STEP_US = 2.5;

/** v68: what this viewer can take of a recipe's set-level extras (absent ⇒ all of it). */
export interface RecipeCaps {
  ringPoseV68: boolean;
  ringLookV68: boolean;
  ringJunctionV68: boolean;
}
const ALL_V68: RecipeCaps = { ringPoseV68: true, ringLookV68: true, ringJunctionV68: true };

/**
 * The set a recipe makes, keeping the WEARER's own fields: ring 0 keeps its
 * size and inscription; the head is dropped (a recipe is a pair of bands —
 * `loadRingState` takes an absent block off), and ring labels are reset to
 * plain wedding bands.
 *
 * The PARTNER's size (v68 rule): the set's ring 1 keeps its size when it is
 * the wearer's — i.e. unless it still equals `current.recipePartnerSizeUS`,
 * the size the LAST recipe gave it — and otherwise follows this recipe: ring
 * 0's plus `recipe.partnerSizeDeltaUS` (absent ⇒ `PARTNER_SIZE_STEP_US`). So a
 * recipe-made partner follows the next recipe, and a partner the user sized is
 * kept. The doc records the size it gave in `recipePartnerSizeUS`.
 *
 * The rings are restored with `loadRingState`, i.e. as WHOLE saved states: any
 * optional block a recipe does not carry (free stones, an inner stone, a
 * pattern, extra grooves …) is cleared, so a recipe looks the same whatever
 * ring it was applied over.
 *
 * v68 extras the viewer cannot take (`caps`) are left out with a sentence in
 * `left`: the photographed pose, the look, the bead options, the thin seat rim.
 */
export function recipeSetDoc(
  recipe: RingRecipe,
  current: {
    ring0: Pick<RingState, "ringSizeUS" | "engravingText"> & { engraving?: Pick<RingState["engraving"], "logo"> };
    partnerSizeUS?: number;
    recipePartnerSizeUS?: number;
  },
  caps: RecipeCaps = ALL_V68,
): RingSetDoc & { left: string[] } {
  const left: string[] = [];
  const [design, partner] = recipe.rings;
  const ring = (p: RingDesignPatch, sizeUS: number, text: string) => {
    let patch = p;
    if (!caps.ringJunctionV68 && p.diamond?.junctionOptions) {
      const { junctionOptions: _jo, ...diamond } = p.diamond;
      patch = { ...p, diamond };
      if (!left.includes(BEADS_LEFT)) left.push(BEADS_LEFT);
    }
    // v68 `StonePattern.seatRimMM` rides the same caps alias: an older viewer's
    // clamp would drop it anyway, but the host says so instead of letting the
    // row quietly sit further in.
    if (!caps.ringJunctionV68 && patch.stonePattern && "seatRimMM" in patch.stonePattern) {
      const { seatRimMM: _rim, ...stonePattern } = patch.stonePattern;
      patch = { ...patch, stonePattern };
      if (!left.includes(RIM_LEFT)) left.push(RIM_LEFT);
    }
    return { ...patch, ringSizeUS: sizeUS, engravingText: text, ringType: "wedding" } as unknown as RingState;
  };
  const first = ring(design, current.ring0.ringSizeUS, current.ring0.engravingText);
  // The wearer's logo is personal like the inscription: it stays on ring 0
  // (the recipe's own mark, if it has one, gives way to it). It used to be
  // cleared by the restore while the size and text were kept.
  const ownLogo = current.ring0.engraving?.logo;
  if (ownLogo) first.engraving = { ...(first.engraving ?? ({} as RingState["engraving"])), logo: ownLogo } as RingState["engraving"];
  const rings: RingState[] = [first];
  let recipePartnerSizeUS: number | undefined;
  if (partner) {
    const wearers = current.partnerSizeUS !== undefined && current.partnerSizeUS !== current.recipePartnerSizeUS;
    const size = wearers
      ? current.partnerSizeUS!
      : Math.min(16, current.ring0.ringSizeUS + (recipe.partnerSizeDeltaUS ?? PARTNER_SIZE_STEP_US));
    if (!wearers) recipePartnerSizeUS = size;
    rings.push(ring(partner, size, ""));
  }
  const doc: RingSetDoc & { left: string[] } = { rings, layout: recipe.layout, linked: false, left };
  if (recipePartnerSizeUS !== undefined) doc.recipePartnerSizeUS = recipePartnerSizeUS;
  if (recipe.pose) {
    if (caps.ringPoseV68) doc.pose = recipe.pose;
    else left.push("The photographed pose was left out: this viewer is too old for it.");
  }
  const look = recipe.look ?? "studio";
  if (caps.ringLookV68) doc.look = look;
  else if (look !== "studio") left.push("The catalogue look was left out: this viewer is too old for it.");
  return doc;
}

const BEADS_LEFT = "Bead options were left out: this viewer is too old for them.";
const RIM_LEFT = "The edge-hugging stone row was left out: this viewer is too old for it, so the row sits a little further in.";

/** The host's ring rows for a restored set — one per ring the VIEWER now holds,
 * so the switcher and the manager list exactly the rings that exist. */
export function rowsFor(doc: Pick<RingSetDoc, "rings">): RingRow[] {
  return doc.rings.map((s) => ({ type: s.ringType ?? "wedding", ...(s.ringName ? { name: s.ringName } : {}), visible: true }));
}

/** Where a recipe's rendered thumbnail lives (static, under `public/`). */
export const recipeThumbUrl = (id: string) => `/ring-designs/${id}.webp`;

const METAL_TINT: Record<string, { body: string; edge: string }> = {
  yellowGold: { body: "#e7cf8f", edge: "#b8964a" },
  roseGold: { body: "#e9b9a2", edge: "#b77c62" },
  whiteGold: { body: "#e4e4e1", edge: "#a9a9a5" },
  platinum: { body: "#dfe1e2", edge: "#a2a6a8" },
};

/**
 * The card's fallback picture until the rendered webp exists: the pair drawn
 * as two bands in their metal, the stones on the front one. A diagram of the
 * recipe's own numbers (width, count, spread, row position), not a render.
 */
export function recipeThumbSvg(recipe: RingRecipe): string {
  const W = 160, H = 120;
  const band = (p: RingDesignPatch, cx: number, cy: number, rx: number, ry: number, front: boolean) => {
    const tint = METAL_TINT[p.materials?.[0]?.presetId ?? "yellowGold"] ?? METAL_TINT.yellowGold;
    const w = Math.max(4, Math.min(16, (p.widthMM ?? 3) * 3.2));
    let out = `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="${tint.edge}" stroke-width="${w + 2}"/>`
      + `<ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="${tint.body}" stroke-width="${w}"/>`;
    if (!front) return out;
    const d = p.diamond ?? {};
    const style = d.style ?? "none";
    if (style === "none") return out;
    const spread = style === "memoir" ? 360 : style === "pattern" ? 140 : Math.max(20, d.spreadDeg ?? 90);
    const count = style === "pattern"
      ? Math.max(3, Math.min(25, p.stonePattern?.layers?.reduce((s, l) => s + (l.count ?? 7), 0) ?? 7))
      : d.count && d.count > 0 ? d.count : Math.round(spread / 12);
    const sizeR = Math.max(1.6, Math.min(4, (d.stoneSizeMM ?? 1.3) * 1.5));
    const off = (d.rowPosition ?? 0) * (w / 2);
    for (let i = 0; i < count; i++) {
      // Front half of the ellipse (the visible arc), centred at the bottom.
      const t = count === 1 ? 0 : -0.5 + i / (count - 1);
      const a = Math.PI / 2 + ((t * Math.min(spread, 180)) * Math.PI) / 180;
      const x = cx + rx * Math.cos(a);
      const y = cy + ry * Math.sin(a) + off;
      out += `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${sizeR.toFixed(1)}" fill="#ffffff" stroke="#9aa7b4" stroke-width="0.8"/>`;
    }
    return out;
  };
  const [a, b] = recipe.rings;
  const back = b ? band(b, 98, 52, 40, 30, false) : "";
  const front = band(a, b ? 66 : 80, b ? 70 : 62, 42, 32, true);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}"><rect width="${W}" height="${H}" fill="#f7f6f3"/>${back}${front}</svg>`;
}

export const recipeThumbDataUrl = (recipe: RingRecipe) => `data:image/svg+xml;utf8,${encodeURIComponent(recipeThumbSvg(recipe))}`;
